import { useEffect } from "react";
import styled from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { templatePageDiv } from "../styles/style";
import SectionHeader from "../components/SectionHeader";
import { applyPolyfills, defineCustomElements } from "@uit/glitch-image/loader";

import avatar from "../img/avatar.svg";
import avatar2 from "../img/avatar-2.svg";
import Bubbles from "../components/Bubbles";

const About = () => {
  const items = [
    "C/C++",
    "Python",
    "JavaScript",
    "SQL",
    "Java",
    "React",
    "Node.js",
    "HTML/CSS",
  ];

  applyPolyfills().then(() => {
    defineCustomElements();
  });

  const fade = {
    visible: { opacity: 1 },
    hidden: { opacity: 0 },
  };

  const controls = useAnimation();
  const [ref, inView] = useInView();

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }

    return () => {
      controls.start("hidden");
    };
  }, [controls, inView]);

  return (
    <AboutPage id="about">
      <SectionHeader title={"ABOUT ME"} />

      <div className="container container-flex">
        <motion.div
          className="shell"
          ref={ref}
          animate={controls}
          initial="hidden"
          transition={{ duration: 1 }}
          variants={fade}
        >
          <SectionHeader title={"ABOUT ME"} />

          <ImageContainer>
            <div className="a1">
              <glitch-image src={avatar} alt="avatar" />
            </div>

            <div className="a2">
              <glitch-image src={avatar2} alt="avatar-blink" />
            </div>
            <p> Toronto, ON</p>
          </ImageContainer>

          <TextContainer>
            <div className="info">
              <p>
                I'd like to consider myself as someone who experiments with
                different technologies to create unique digital media. Although
                my current focus is in web development and UI designs, I also
                like to dabble in some machine learning and automation. I
                believe the possibilities are endless within this field and I'm
                here to learn and explore all of it.
              </p>
            </div>
            <div className="bubbles">
              {items.map((e) => {
                return <Bubbles item={e} key={e} />;
              })}
            </div>
          </TextContainer>
        </motion.div>
      </div>
    </AboutPage>
  );
};

const AboutPage = styled(templatePageDiv)`
  background: #0c0c0c;

  @media (max-width: 900px) {
    h2 {
      display: none;
    }
  }

  .container {
    min-height: 100vh;
    position: relative;
  }

  .shell {
    display: flex;
    align-items: center;
    padding: 3rem 0rem;
    position: relative;

    h2 {
      display: none;
    }

    @media (max-width: 900px) {
      h2 {
        display: flex;
      }
    }

    .aboutme-mobile {
      display: flex;
      top: 0;
      left: 0;
      align-items: center;
    }

    @media (max-width: 900px) {
      flex-direction: column;
    }
  }
`;

const ImageContainer = styled(motion.div)`
  padding-right: 8rem;

  .a2 {
    display: none;
  }

  :hover {
    .a1 {
      display: none;
    }

    .a2 {
      display: flex;
    }
  }

  @media (max-width: 1100px) {
    padding-right: 5rem;
  }

  @media (max-width: 900px) {
    padding: 0;
    padding-bottom: 1.5rem;

    p {
      text-align: center;
    }
  }

  .a1,
  .a2 {
    width: 15rem;

    @media (max-width: 1100px) {
      width: 12rem;
    }

    @media (max-width: 900px) {
      width: 8rem;
    }

    @media (max-width: 400px) {
      width: 6rem;
    }
  }

  p {
    padding-top: 2rem;
    font-weight: bold;

    @media (max-width: 900px) {
      padding-top: 0.5rem;
    }

    @media (max-width: 400px) {
      font-size: 0.8rem;
    }
  }
`;

const TextContainer = styled(motion.div)`
  padding-left: 8rem;
  border-left: 5px solid white;

  @media (max-width: 1100px) {
    padding-left: 5rem;
  }

  @media (max-width: 900px) {
    padding: 0;
    border: none;
    text-align: center;
  }

  .info {
    font-size: 1rem;

    p {
      line-height: 1.3rem;

      @media (max-width: 1250px) {
        font-size: 0.9rem;
      }

      @media (max-width: 400px) {
        font-size: 0.7rem;
      }
    }
  }

  .bubbles {
    display: flex;
    flex-wrap: wrap;
    padding-top: 0.3rem;

    @media (max-width: 900px) {
      border: none;
      text-align: center;
      justify-content: center;
      align-items: center;
    }
  }
`;

export default About;
